import React, { useState, useEffect, useCallback, useRef } from "react";
import CardTable from "../../../../components/common/CardTable";
import { useAppSelector } from "../../../../store/hooks";
import formatDate from "../../../../utils/formatDate";
import Table, { TableColumn, TableRow } from "../../../../components/common/Table/Table";
import Pagination from "../../../../components/common/Table/Pagination";
import Loader from "../../../../components/common/Loader";
import NoResultsFound from "../../../../components/common/NoResultsFound";
import  { DropdownOption } from "../../../../components/common/Dropdown";
import  { ValidationError } from "../../../../components/common/ErrorBanner";
import useDeleteModal from "../ProjectHelper/useDeleteModal";
import DeleteModal from "../ProjectHelper/DeleteModal";
import DeleteBanner from "../ProjectHelper/DeleteBanner";
import TestDataHelpModal from "../ProjectHelper/TestDataHelpModal";
import {
    ProjectFailureBanner,
    ProjectSuccessBanner,
    useProjectBanner,
} from "../ProjectHelper/ProjectStatusBanner";
import ManageTestData from '../Container/ManageTestData';
import { formatTimestampMDY } from "../../../../utils/date";
import { validateDate } from "../projectValidation";
import {
  hasTestDataFormChanged,
  createTestDataFormSnapshot,
  type TestDataFormData,
} from '../../../../utils/ProjectDetails/projectDetailsValidation';
const NO_SELECTED_TEST_OF_CHANGE_MESSAGE = "Please select a Test Of Change to view Test Data";

// shift options and defaultOption are provided via `extraProps` from parent `DetailsContainer`

// `useDeleteModal` will be called inside the component to follow Hooks rules

export const TestDataTab: React.FC<{
    canEdit?: boolean;
    canDelete?: boolean;
    project?: any;
    selectedTestOfChange?: any;
    testDataSeriesItems?: any[];
    testDataSeriesCurrentPage?: number;
    testDataSeriesPerPage?: number;
    testDataSeriesLoading?: boolean;
    testDataSeriesError?: string | null;
    testDataSeriesTotalItems?: number;
    onSetTestDataSeriesPage?: (p: number) => void;
    onSetTestDataSeriesSort?: (col: string, dir: "asc" | "desc") => void;
    testDataSeriesUiSortColumn?: string;
    testDataSeriesSortDirection?: "asc" | "desc";
    onAddTestData?: (payload: any) => Promise<{ success: boolean; error?: any }>;
    onDeleteTestDataSeries?: (seriesId: number, tocId?: number) => Promise<{ success: boolean; error?: any }>;
    shiftOptions?: DropdownOption[];
    defaultOption?: DropdownOption;
    onSetUnsavedChanges?: (hasChanges: boolean) => void;
}> = ({ canEdit = false, canDelete = false, project, selectedTestOfChange, testDataSeriesItems = [], testDataSeriesCurrentPage = 1, testDataSeriesPerPage = 5, testDataSeriesLoading = false, testDataSeriesError = null, testDataSeriesTotalItems = 0, onSetTestDataSeriesPage, onSetTestDataSeriesSort, testDataSeriesUiSortColumn = "testDate", testDataSeriesSortDirection = "desc", onAddTestData, onDeleteTestDataSeries, shiftOptions = [], defaultOption = { id: 0, label: 'Select', value: 'Select' }, onSetUnsavedChanges }) => {
    const reduxFirst = useAppSelector((s) => s.testsOfChangeSlice.testOfChangeList?.[0] ?? null);
    // onAddTestData is provided by parent DetailsContainer via Tabs.extraProps
    // destructured from component props above
    const selected = selectedTestOfChange ?? reduxFirst;

    const [uiSortColumn, setUiSortColumn] = useState<string>(testDataSeriesUiSortColumn ?? "testDate");
    const [testDataDate, setTestDataDate] = useState('');
    const [testDataDateError, setTestDataDateError] = useState('');
    const {
        showDeleteModal,
        setShowDeleteModal,
        deletedResponseName,
        setDeletedResponseName,
        showDeleteBanner,
        setShowDeleteBanner,
        responseForms,
        setResponseForms,
    } = useDeleteModal();
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">(testDataSeriesSortDirection ?? "desc");
    const [currentTdsId, setCurrentTdsId] = useState<number | null>(null);
    const [selectedShift, setSelectedShift] = useState<DropdownOption>(defaultOption);

    const baseColumns: TableColumn[] = [
        { key: "testDate", label: "Date Of Test", isSortable: true },
        { key: "shift", label: "Shift", className: "md:max-w-[7rem]", isSortable: true },
        { key: "numeratorValue", label: project?.numeratorName ?? "", isSortable: true },
        { key: "denominatorValue", label: project?.denominatorName ?? "", isSortable: true },
        { key: "value", label: project?.valueName ?? ""},
        { key: "notes", label: "Notes", isSortable: true },
        { key: "outsideVarValue", label: project?.outsideVarName != null && String(project.outsideVarName).trim() !== "" ? project.outsideVarName : "Other", isSortable: true },
    ];

    const columns: TableColumn[] = [
        ...baseColumns,
        ...(canDelete && canEdit ? [{ key: "deleteIcon", label: "Delete" }] : []),
        ...(canEdit ? [{ key: "edit", label: "Edit" }] : []),
    ];

    // keep local UI sort in sync if parent updates it
    useEffect(() => {
        setUiSortColumn(testDataSeriesUiSortColumn ?? "testDate");
    }, [testDataSeriesUiSortColumn]);
    useEffect(() => {
        setSortDirection(testDataSeriesSortDirection ?? "desc");
    }, [testDataSeriesSortDirection]);

    const getDeleteButton = (testData: any) => (
        <button
            type="button"
            onClick={() => handleDeleteClick(testData)}
            className="bg-transparent border-0 p-0 cursor-pointer"
            aria-label="Delete"
        >
            <img
                src="/assets/images/trash-outline.svg"
                alt="Delete icon"
                className="min-w-6 min-h-6"
                width={24}
                height={24}
                style={{ display: 'block' }}
            />
        </button>
    );

    const handleDeleteClick = useCallback((selectedRowOrId: any) => {
        let testDataObj: any = null;
        if (selectedRowOrId && typeof selectedRowOrId === 'object') {
            testDataObj = selectedRowOrId;
        } else {
            const id = Number(selectedRowOrId);
            if (!Number.isNaN(id)) {
                testDataObj = testDataSeriesItems?.find((t: any) => String(t.tdsId) === String(id)) ?? null;
            }
        }
        const testDataId = Number(testDataObj?.tdsId ?? selectedRowOrId);
        if (Number.isNaN(testDataId) && testDataId !== 0) return

        setCurrentTdsId(testDataId);
        setResponseForms([
            {
                id: String(testDataId),
                title: `Test Data Series ID: ${testDataId}`,
                subject: "",
                message: "",
            },
        ]);
        setShowDeleteModal(true);
    }, [testDataSeriesItems]);
    const resolveTestDataObj = React.useCallback((selectedRowOrId: any) => {
        if (selectedRowOrId && typeof selectedRowOrId === 'object') return selectedRowOrId;
        const id = Number(selectedRowOrId);
        if (!Number.isNaN(id)) {
            return testDataSeriesItems?.find((t: any) => String(t.tdsId) === String(id)) ?? null;
        }
        return null;
    }, [testDataSeriesItems]);

    const prefillFormFromTestData = React.useCallback((testDataObj: any) => {
        setNumeratorName(testDataObj?.numeratorValue == null ? "" : String(testDataObj.numeratorValue));
        setDenomenatorName(testDataObj?.denominatorValue == null ? "" : String(testDataObj.denominatorValue));
        setNotes(testDataObj?.notes ?? "");

        const dateVal = testDataObj?.testDate ?? testDataObj?.dateLastUpd ?? "";
        if (typeof dateVal === 'string' && /^\d{2}-\d{2}-\d{4}$/.test(dateVal)) {
            setTestDataDate(dateVal);
        } else if (dateVal) {
            const formatted = formatTimestampMDY(dateVal);
            setTestDataDate(formatted === '-' ? String(dateVal) : formatted);
        } else {
            setTestDataDate('');
        }

        const shiftKey = (testDataObj?.shift ?? testDataObj?.shift ?? "");
        const matched = shiftOptions.find((opt) => String(opt.value).toLowerCase() === String(shiftKey).toLowerCase());
        setSelectedShift(matched ?? defaultOption);

        if (testDataObj?.outsideVarValue == null) {
            setOutSiderVarVal("");
        } else {
            setCalculateVal(Number(testDataObj.outsideVarValue));
            setOutSiderVarVal(String(testDataObj.outsideVarValue));
        }

        setValidationErrors([]);
        setShowAddForm(true);
        
        // Mark prepopulation as complete for snapshot timing
        setIsPrepopulationComplete(true);
    }, [shiftOptions, defaultOption]);

    const handleEditClick = useCallback((selectedRowOrId: any) => {
        const testDataObj = resolveTestDataObj(selectedRowOrId);
        const testDataId = Number(testDataObj?.tdsId ?? selectedRowOrId);
        if (Number.isNaN(testDataId) && testDataId !== 0) return;

        setCurrentTdsId(testDataId);
        prefillFormFromTestData(testDataObj);

        setTimeout(() => {
            manageTestDataRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 0);
    }, [resolveTestDataObj, prefillFormFromTestData]);

        

        const handleRangeTestDataDateChange = (_name: string, value: string) => {
            setTestDataDate(value);
            const validation = validateDate(value);
            setTestDataDateError(validation.error);
            // Clear the top-banner entry for testDate as soon as the user edits the field
            setValidationErrors((prev) => prev.filter((e) => e.field !== 'testDate'));
        };

    const handleCancelDelete = useCallback(() => {
        setShowDeleteModal(false);
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        if (!currentTdsId) return;

        // Close modal immediately so UI does not block while async delete runs
        setShowDeleteModal(false);
        try {
            if (typeof (onDeleteTestDataSeries) === 'function') {
                await (onDeleteTestDataSeries as any)(currentTdsId);
            }

            // Save the current response name before deleting
            const deletedResponseName = responseForms[0]?.title || '';
            // Show delete success banner
            setDeletedResponseName(deletedResponseName);
            setShowDeleteBanner(true);

            // Hide banner after 5 seconds
            setTimeout(() => {
                setShowDeleteBanner(false);
            }, 5000);

            // Reset to empty form
            setResponseForms([{ id: '1', title: '', subject: '', message: '' }]);
        } catch (error) {
            console.error('Error deleting TDS via parent handler:', error);
        }
    }, [currentTdsId]);

        const getEditButton = (testData: any) => (
            <button
                type="button"
                onClick={() => handleEditClick(testData)}
                className="bg-transparent border-0 p-0 cursor-pointer"
                aria-label="Edit"
            >
                <img
                    src="/assets/images/pencil-outline.svg"
                    alt="Edit icon"
                    className="min-w-6 min-h-6"
                    width={24}
                    height={24}
                    style={{ display: 'block' }}
                />
            </button>
        );


    const rowsWithActions: TableRow[] = testDataSeriesItems.map((testData, index) => {
        const numerator = Number.parseFloat(String(testData.numeratorValue ?? ""));
        const denominator = Number.parseFloat(String(testData.denominatorValue ?? ""));
        const calculatedValue = !Number.isNaN(numerator) && !Number.isNaN(denominator) && denominator !== 0
            ? numerator / denominator
            : 0;
        const hasCustomLabel = project?.outsideVarName != null && String(project.outsideVarName).trim() !== "";
        const outsideVarDisplay = (() => {
            if (testData.outsideVarValue != null && String(testData.outsideVarValue).trim() !== "") {
                return testData.outsideVarValue;
            }
            return hasCustomLabel ? "" : "NA";
        })();

        return {
            rowKey: `testData-${testData.tdsId}-0-${index}`,
            testDate: formatDate(testData.testDate, "MM-DD-YYYY") ?? "",
            shift: testData.shift ?? "",
            numeratorValue: testData.numeratorValue ?? "",
            denominatorValue: testData.denominatorValue ?? "",
            value: calculatedValue === 0 ? "0.00" : calculatedValue.toFixed(4),
            notes: testData.notes ?? "",
            outsideVarValue: outsideVarDisplay,
            deleteIcon: canEdit && canDelete ? getDeleteButton(testData) : null,
            edit: canEdit ? getEditButton(testData) : null,
        }
    });


    const handleTestDataSeriesListSort = useCallback(
        (columnKey: string, direction: "asc" | "desc") => {
            const apiColumnMap: { [key: string]: string } = {
                'testDate': 'testDate',
                'shift': 'shift',
                'notes': 'notes',
                'denominatorValue': 'denominatorValue',
                'numeratorValue': 'numeratorValue',
                'outsideVarValue': 'outsideVarValue',
            };
            const apiSortColumn = apiColumnMap[columnKey] ?? columnKey.toUpperCase();
            setUiSortColumn(columnKey);
            setSortDirection(direction);
            if (onSetTestDataSeriesSort) onSetTestDataSeriesSort(apiSortColumn, direction);
            if (onSetTestDataSeriesPage) onSetTestDataSeriesPage(1);
        },
        [onSetTestDataSeriesPage, onSetTestDataSeriesSort],
    );

    const handleTestDataSeriesPageChange = useCallback((page: number) => {
        if (onSetTestDataSeriesPage) onSetTestDataSeriesPage(page);
    }, [onSetTestDataSeriesPage]);


    const renderTestDataSeries = () => {
        if (testDataSeriesLoading) {
            return <Loader />;
        }

        if (testDataSeriesError) {
            return (
                <div className="flex flex-col items-center justify-center min-h-[400px] px-4">
                    <img
                        src="/assets/images/alertSolid.svg"
                        alt="error-icon"
                        className="w-16 h-16 mb-4"
                    />
                    <h3 className="h3 text-center mb-2">Something Went Wrong</h3>
                </div>
            );
        }

        return (
            <div className="flex flex-col gap-[32px] flex-wrap mt-[36px]! w-full">
                <CardTable>
                    <>
                        <Table
                            columns={columns}
                            rows={rowsWithActions}
                            onSort={(columnKey, sortDirection) =>
                                handleTestDataSeriesListSort(columnKey, sortDirection.toLowerCase() as "asc" | "desc")
                            }
                            sortColumn={uiSortColumn}
                            sortDirection={sortDirection.toUpperCase() as "ASC" | "DESC"}
                        />
                        <div className="mt-4 mobile:relative mobile:left-7.5">
                            <div className="mt-4 mobile:relative mobile:left-7.5">
                                <Pagination
                                    totalItems={testDataSeriesTotalItems}
                                    itemsPerPage={testDataSeriesPerPage}
                                    currentPage={testDataSeriesCurrentPage}
                                    onPageChange={handleTestDataSeriesPageChange}
                                />
                            </div>
                        </div>
                    </>
                </CardTable>
            </div>
        )
    };

    const validateDecimalFormat = React.useCallback((v: string) => {
        if (!v) return true;
        // Final/strict check: up to 10 digits before decimal, optional decimal with up to 2 digits after
        return /^(\d{1,10}(\.\d{0,2})?|\.\d{1,2})$/.test(v);
    }, []);

    const sanitizeDecimalInput = React.useCallback((v: string) => {
        if (!v) return '';
        // remove invalid chars, allow only digits and a single dot
        // use split+join instead of replace with global regex to satisfy Sonar rule S7781
        let s = v.split(/[^0-9.]/).join('');
        const parts = s.split('.');
        if (parts.length > 1) {
            s = parts[0] + '.' + parts.slice(1).join('');
        }
        const [intPart = '', fracPart = ''] = s.split('.');
        const intTrunc = intPart.slice(0, 10);
        const fracTrunc = fracPart.slice(0, 2);
        if (s.includes('.')) {
            if (fracTrunc.length) {
                return intTrunc + '.' + fracTrunc;
            }
            return intTrunc + '.';
        }
        return intTrunc;
    }, []);

    // testDataDate moved here
    const [numeratorName, setNumeratorName] = useState<string>("");
    const [denomenatorName, setDenomenatorName] = useState<string>("");
    const [outSiderVarVal, setOutSiderVarVal] = useState<string>("");
    const [calculateVal, setCalculateVal] = useState<number>(0);
    const [notes, setNotes] = useState<string>("");
    const [showAddForm, setShowAddForm] = useState<boolean>(false);
    const {
        bannerRef: successBannerRef,
        isVisible: showSuccessBanner,
        message: successMessage,
        showBanner: triggerSuccessBanner,
        hideBanner: hideSuccessBanner,
    } = useProjectBanner("Test data was saved successfully.");
    const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
    const testDateRef = React.useRef<HTMLInputElement>(null);
    const shiftRef = React.useRef<HTMLDivElement>(null);
    const numeratorRef = React.useRef<HTMLInputElement>(null);
    const denominatorRef = React.useRef<HTMLInputElement>(null);
    const notesRef = React.useRef<HTMLTextAreaElement>(null);
    const manageTestDataRef = React.useRef<HTMLDivElement>(null);
    const {
        bannerRef: errorBannerRef,
        isVisible: showErrorBanner,
        message: errorMessage,
        showBanner: triggerErrorBanner,
        hideBanner: hideErrorBanner,
    } = useProjectBanner("An error occurred while saving test data.");

    const handleSelectedShiftClick = (selectedOption: DropdownOption) => {
        setSelectedShift(selectedOption);
        // Clear the top-banner entry for shift as soon as the user selects a value
        setValidationErrors((prev) => prev.filter((e) => e.field !== 'shift'));
    };

    /**
     * Clears the 'numerator' validation error as soon as the user edits the field.
     */
    const handleNumeratorChange = useCallback((s: string) => {
        setNumeratorName(s);
        setValidationErrors((prev) => prev.filter((e) => e.field !== 'numerator'));
    }, []);

    /**
     * Clears the 'denominator' validation error as soon as the user edits the field.
     */
    const handleDenominatorChange = useCallback((s: string) => {
        setDenomenatorName(s);
        setValidationErrors((prev) => prev.filter((e) => e.field !== 'denominator'));
    }, []);

    useEffect(() => {
        // Calculate value when numerator or denominator changes
        const num = Number.parseFloat(numeratorName);
        const denom = Number.parseFloat(denomenatorName);
        if (!Number.isNaN(num) && !Number.isNaN(denom) && denom !== 0) {
            setCalculateVal(num / denom);
        } else {
            setCalculateVal(0);
        }
    }, [numeratorName, denomenatorName]);

    // ===== Form Change Tracking =====
    const initialFormSnapshotRef = useRef<Partial<TestDataFormData> | null>(null);
    const isFormOpenRef = useRef<boolean>(false);
    const [isPrepopulationComplete, setIsPrepopulationComplete] = useState<boolean>(false);

    /**
     * Get current form data for change comparison
     */
    const getCurrentFormData = useCallback((): Partial<TestDataFormData> => {
      return {
        testDataDate,
        selectedShift,
        numeratorName,
        denomenatorName,
        notes,
        outSiderVarVal,
      };
    }, [testDataDate, selectedShift, numeratorName, denomenatorName, notes, outSiderVarVal]);

    /**
     * Create initial snapshot when form opens (add or edit mode)
     */
    useEffect(() => {
      // Only create snapshot when form is shown
      if (showAddForm && !initialFormSnapshotRef.current) {
        // For Add mode (currentTdsId === null), create snapshot immediately
        // For Edit mode (currentTdsId !== null), wait until prepopulation is complete
        const canCreateSnapshot = (currentTdsId === null) || (currentTdsId !== null && isPrepopulationComplete);
        
        if (canCreateSnapshot) {
          const currentData = getCurrentFormData();
          initialFormSnapshotRef.current = createTestDataFormSnapshot(currentData);
          isFormOpenRef.current = true;
        }
      }

      // Reset snapshot when form closes
      if (!showAddForm && initialFormSnapshotRef.current) {
        initialFormSnapshotRef.current = null;
        isFormOpenRef.current = false;
        if (typeof onSetUnsavedChanges === 'function') {
          onSetUnsavedChanges(false);
        }
      }
    }, [showAddForm, currentTdsId, isPrepopulationComplete, getCurrentFormData, onSetUnsavedChanges]);

    /**
     * Track form changes and notify parent component
     */
    useEffect(() => {
      // Only track changes after the form has been opened and snapshot created
      if (!isFormOpenRef.current || !initialFormSnapshotRef.current) {
        return;
      }

      const currentData = getCurrentFormData();
      const hasChanges = hasTestDataFormChanged(currentData, initialFormSnapshotRef.current);

      if (typeof onSetUnsavedChanges === 'function') {
        onSetUnsavedChanges(hasChanges);
      }
    }, [getCurrentFormData, onSetUnsavedChanges, testDataDate, selectedShift, numeratorName, denomenatorName, notes, outSiderVarVal]);

    /**
     * Reset unsaved changes state when form is closed or saved successfully
     */
    const resetUnsavedChanges = useCallback(() => {
      initialFormSnapshotRef.current = null;
      isFormOpenRef.current = false;
      setIsPrepopulationComplete(false);
      if (typeof onSetUnsavedChanges === 'function') {
        onSetUnsavedChanges(false);
      }
    }, [onSetUnsavedChanges]);
    // ===== End Form Change Tracking =====

    // ===== TOC Change Reset =====
    // When the selected Test of Change changes, any in-progress (unsaved) form data
    // must be discarded so the user always starts with a fresh form for the new TOC.
    const prevSelectedTocIdRef = useRef<string | number | null | undefined>(undefined);

    useEffect(() => {
      const tocId = selectedTestOfChange?.tocId ?? selectedTestOfChange?.id ?? null;

      // Skip on initial mount — only reset after the first TOC has been set
      if (prevSelectedTocIdRef.current === undefined) {
        prevSelectedTocIdRef.current = tocId;
        return;
      }

      if (tocId !== prevSelectedTocIdRef.current) {
        prevSelectedTocIdRef.current = tocId;
        setTestDataDate("");
        setNumeratorName("");
        setDenomenatorName("");
        setOutSiderVarVal("");
        setNotes("");
        setCalculateVal(0);
        setSelectedShift(defaultOption);
        setCurrentTdsId(null);
        setShowAddForm(false);
        setValidationErrors([]);
        setIsPrepopulationComplete(false);
        resetUnsavedChanges();
      }
    }, [selectedTestOfChange?.tocId, selectedTestOfChange?.id, defaultOption, resetUnsavedChanges]);
    // ===== End TOC Change Reset =====

    const validateFormFields = (): ValidationError[] => {
        const errors: ValidationError[] = [];
        if (!testDataDate || testDataDate.trim() === "") {
            errors.push({ field: 'testDate', message: 'Date of Test is required.', fieldRef: testDateRef });
        } else {
            const dv = validateDate(testDataDate);
            if (!dv.isValid) errors.push({ field: 'testDate', message: dv.error, fieldRef: testDateRef });
        }
        if (!selectedShift?.value || selectedShift.value === 'Select') {
            errors.push({ field: 'shift', message: 'Shift is required.', fieldRef: shiftRef });
        }
        const num = Number.parseFloat(numeratorName as any);
        if (!numeratorName || numeratorName.toString().trim() === '' || Number.isNaN(num)) {
            errors.push({ field: 'numerator', message: 'Numerator is required and must be numeric.', fieldRef: numeratorRef });
        }
        const denom = Number.parseFloat(denomenatorName as any);
        if (!denomenatorName || denomenatorName.toString().trim() === '' || Number.isNaN(denom)) {
            errors.push({ field: 'denominator', message: 'Denominator is required and must be numeric.', fieldRef: denominatorRef });
        }
        // check strict decimal format (<=10 digits before, <=2 after)
        if (denomenatorName && !Number.isNaN(denom) && !validateDecimalFormat(denomenatorName)) {
            errors.push({ field: 'denominator', message: 'Denominator must be a valid decimal (max 10 digits before, 2 after).', fieldRef: denominatorRef });
        }
        // also validate numerator strict format
        if (numeratorName && !validateDecimalFormat(numeratorName)) {
            errors.push({ field: 'numerator', message: 'Numerator must be a valid decimal (max 10 digits before, 2 after).', fieldRef: numeratorRef });
        }
        return errors;
    };

    const handleValidationErrors = (errors: ValidationError[]): void => {
        setValidationErrors(errors);
        setShowAddForm(true);
        const firstRef = errors[0].fieldRef;
        if (firstRef?.current) {
            try {
                firstRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                (firstRef.current as any).focus?.();
            } catch (err) {
                console.error("Error focusing field for validation error:", err);
            }
        }
    };

    const buildPayload = () => {
        return {
            tocId: selected?.tocId ?? selected?.id ?? null,
            seriesId: currentTdsId ?? undefined,
            // testDataDate is already a canonical MM-DD-YYYY string from the date
            // picker — do not round-trip it through formatDate/new Date(), which
            // parses dash-formatted strings as local time and can shift the day
            // for viewers in positive-UTC-offset timezones.
            testDate: testDataDate,
            shiftId: selectedShift.value,
            notes,
            numeratorValue: Number(numeratorName),
            denominatorValue: Number(denomenatorName),
            outsideVarValue: outSiderVarVal ?? "",
        };
    };

    const resetForm = (): void => {
        setTestDataDate("");
        setNumeratorName("");
        setDenomenatorName("");
        setOutSiderVarVal("");
        setNotes("");
        setCalculateVal(0);
        setValidationErrors([]);
    };

    const handleAddTestDataSuccess = (keepOpen: boolean, isEdit = false): void => {
        resetForm();
        resetUnsavedChanges();
        triggerSuccessBanner(isEdit ? "Test data was updated successfully." : "Test data was saved successfully.");
        if (onSetTestDataSeriesPage) onSetTestDataSeriesPage(1);
        if (!keepOpen) setShowAddForm(false);
    };

    const extractApiMessage = (source: any, fallback: string): string => {
        if (!source) return fallback;

        const candidates = [
            source,
            source?.error,
            source?.payload,
            source?.response?.data,
            source?.error?.response?.data,
            source?.payload?.response?.data,
        ];

        for (const candidate of candidates) {
            if (!candidate) continue;

            if (Array.isArray(candidate?.details)) {
                const detailsMessage = candidate.details.find((detail: any) => typeof detail?.message === 'string')?.message;
                if (detailsMessage) return detailsMessage;
            }

            if (typeof candidate?.message === 'string' && candidate.message.trim() !== '') {
                return candidate.message;
            }

            if (typeof candidate?.error === 'string' && candidate.error.trim() !== '') {
                return candidate.error;
            }
        }

        return fallback;
    };

    const extractErrorMessage = (err: unknown): string => {
        if (err instanceof Error) {
            return err.message;
        }

        if (typeof err === 'object' && err !== null) {
            return extractApiMessage(err, "Error saving test data.");
        }

        return String(err) || "Error saving test data.";
    };

    const handleAddTestDataError = (result: any): void => {
        const msg = extractApiMessage(result, "Failed to save test data. Please try again.");
        triggerErrorBanner(msg);
    };

    async function handleAddTestData(event?: React.MouseEvent<HTMLButtonElement, MouseEvent>, keepOpen = false): Promise<void> {
        if (event) event.preventDefault();
        const errors = validateFormFields();
        if (errors.length > 0) {
            handleValidationErrors(errors);
            return;
        }


        const payload = buildPayload();

        if (typeof (onAddTestData as any) === "function") {
            try {
                const result = await (onAddTestData as any)(payload);
                if (result?.success) {
                    const isEdit = Boolean(payload?.seriesId);
                    handleAddTestDataSuccess(keepOpen, isEdit);
                } else {
                    handleAddTestDataError(result);
                }
            } catch (err) {
                const msg = extractErrorMessage(err);
                triggerErrorBanner(msg);
            }
        } else {
            resetForm();
            setShowAddForm(false);
        }
    }
    const handleAddTestDataToogle = () => {
        const opening = !showAddForm;
        if (opening) {
            setCurrentTdsId(null);
            resetForm();
            setSelectedShift(defaultOption);
            setValidationErrors([]);
            hideErrorBanner();
            hideSuccessBanner();
            setIsPrepopulationComplete(false);
        } else {
            // Closing the form
            resetUnsavedChanges();
        }
        setShowAddForm(opening);
    };

    if (!(selected && (selected.tocId || selected.testOfChange))) {
        return (
            <div className="mb-4! w-full mt-10!">
                <NoResultsFound
                    dsCardClassName="w-full!"
                    title="Alert"
                    message={NO_SELECTED_TEST_OF_CHANGE_MESSAGE}
                />
            </div>
        );
    }

    if (!project?.numeratorName || !project?.denominatorName) {
        return (
            <div className="mb-4! w-full mt-10!">
                <NoResultsFound dsCardClassName="w-full!" title="Alert" message="Please enter a valid Numerator Label (Top), Denominator Label (Bottom), Calculated Value Label (Top/Bottom) by editing the project details before continuing." />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-[32px] flex-wrap mt-[36px]! w-full">
                <DeleteBanner show={showDeleteBanner} bannerText={deletedResponseName} />
                <ProjectSuccessBanner
                    show={showSuccessBanner}
                    message={successMessage}
                    bannerRef={successBannerRef}
                />
                <ProjectFailureBanner
                    show={showErrorBanner}
                    message={errorMessage}
                    bannerRef={errorBannerRef}
                />
                <div className="pt-4">
                    {selected && (selected.tocId || selected.testOfChange) && (
                        <><div className="pt-4 flex justify-between items-center gap-2 mb-4!">
                            <div className="flex gap-2">
                                <p className="p3"><strong>Name Of Project: </strong></p>
                                <p className="p3">{project?.nameOfProject ?? project?.pipDesc ?? "-"}</p>
                            </div>
                            <TestDataHelpModal />
                        </div><div className="pt-4 flex gap-2 mb-4!">
                                <p className="p3"><strong>Last Updated: </strong></p>
                                <p className="p3">{selected ? formatTimestampMDY(selected.dateLastUpd ?? selected.datelastupd) : "-"}</p>
                            </div><div className="pt-4 flex gap-2 mb-4! ">
                                    <p className="p3"><strong>Test Of Change: </strong></p>
                                    <p className="p3">{selected?.testOfChangeTitle ?? "-"}</p>
                            </div><div className="pt-4 flex gap-2 mb-10!">
                                <p className="p3"><strong>Rating: </strong></p>
                                <p className="p3">{selected?.ratingText ?? selected?.rating ?? "-"}</p>
                            </div></>
                    )}
                    <div>
                        <div className="pb-4" ref={manageTestDataRef}>
                            {canEdit && (
                                <div className="flex mb-4!">
                                    <button
                                        aria-label="Toggle Add Test Data"
                                        className="button py-0! -inverted"
                                        data-analytics-type="button"
                                        data-analytics-click="AddNewTestData"
                                        onClick={handleAddTestDataToogle}
                                    >
                                        Add New Test Data
                                    </button>
                                </div>
                            )}

                            <ManageTestData
                                visible={showAddForm}
                                validationErrors={validationErrors}
                                testDataDate={testDataDate}
                                testDataDateError={testDataDateError}
                                onRangeTestDataDateChange={handleRangeTestDataDateChange}
                                selectedShift={selectedShift}
                                shiftOptions={shiftOptions}
                                defaultOption={defaultOption}
                                handleSelectedShiftClick={handleSelectedShiftClick}
                                numeratorRef={numeratorRef}
                                numeratorName={numeratorName}
                                setNumeratorName={handleNumeratorChange}
                                denominatorRef={denominatorRef}
                                denomenatorName={denomenatorName}
                                setDenomenatorName={handleDenominatorChange}
                                outSiderVarVal={outSiderVarVal}
                                setOutSiderVarVal={setOutSiderVarVal}
                                calculateVal={calculateVal}
                                project={project}
                                notesRef={notesRef}
                                
                                notes={notes}
                                setNotes={setNotes}
                                setShowAddForm={setShowAddForm}
                                handleAddTestData={handleAddTestData}
                                sanitizeDecimalInput={sanitizeDecimalInput} />
                        </div>
                        {renderTestDataSeries()}
                    </div>

                    <div className="mb-4! w-full mt-10!">
                        {(!showAddForm && !testDataSeriesLoading && (!testDataSeriesItems || testDataSeriesItems.length === 0)) && (
                            <NoResultsFound dsCardClassName="w-full!" title="No Test Data" message="The selected Test of Change has no data available." />
                        )}
                    </div>
                </div>
                <div>
                    <DeleteModal isOpen={showDeleteModal} title="Delete Test Data" deleteInfo={"Are you sure you want to delete this test data?"} deleteButtonInfo="Delete Data" onCancel={handleCancelDelete}  onConfirm={handleConfirmDelete} />
                </div>

            </div>


    );
};


